import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent, SlideshowSelectEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';
import { isMobileBreakpoint } from '@theme/utilities';
import { getProductPhotoSwipe } from '@theme/product-photoswipe';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect, { signal });
    this.addEventListener('click', this.#handleGalleryClick, { signal, capture: true });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Pauses all playing videos when the gallery slide changes.
   * @param {SlideshowSelectEvent} event
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.previousIndex === event.detail.index) return;

    this.#pauseAllMedia();
  };

  #pauseAllMedia() {
    for (const media of this.querySelectorAll('deferred-media, product-model')) {
      if (media instanceof HTMLElement && 'pauseMedia' in media && typeof media.pauseMedia === 'function') {
        media.pauseMedia();
      }
    }
  }

  /**
   * Pauses inline videos when clicking a non-video item (desktop grid) or zoom target.
   * @param {Event} event
   */
  #handleGalleryClick = (event) => {
    if (!(event.target instanceof Element)) return;

    if (event.target.closest('.slideshow-controls__thumbnail')) return;

    const mediaContainer = event.target.closest('.product-media-container');
    if (!mediaContainer || !this.contains(mediaContainer)) return;

    if (
      mediaContainer.classList.contains('product-media-container--video') ||
      mediaContainer.classList.contains('product-media-container--external_video')
    ) {
      return;
    }

    this.#pauseAllMedia();
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.#pauseAllMedia();

    if (isMobileBreakpoint() && !this.#isMobileZoomEnabled()) return;

    if (!isMobileBreakpoint()) {
      const configElement = this.querySelector('[data-photoswipe-config]');
      const photoSwipeIndex = configElement
        ? JSON.parse(configElement.textContent).indexMap[index]
        : -1;

      if (photoSwipeIndex >= 0 && getProductPhotoSwipe(this)?.open(index, event)) {
        return;
      }
    }

    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * @returns {boolean}
   */
  #isMobileZoomEnabled() {
    return this.dataset.zoomMobile !== 'false';
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    if (isMobileBreakpoint() && !this.#isMobileZoomEnabled()) return;

    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
